import { useState, type ReactNode } from 'react';
import { ChevronRight, Loader2, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChangeEmailModal } from './ChangeEmailModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import { FeedbackModal } from './FeedbackModal';

interface AccountCardProps {
  email: string;
  onSignOut: () => Promise<void>;
}

export function AccountCard({ email, onSignOut }: AccountCardProps) {
  const { t } = useTranslation();
  const [signingOut, setSigningOut] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await onSignOut();
    setSigningOut(false);
  };

  return (
    <>
      <div className="bg-white rounded-[2.5rem] p-2 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100 flex flex-col">
        <div className="p-5 px-6 border-b border-stone-50">
          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1.5">{t('accountCard.email')}</p>
          <p className="text-[15px] font-bold text-stone-900 tracking-tight">{email || '—'}</p>
        </div>
        <ActionRow label={t('accountCard.changeEmail')} onClick={() => setEmailModalOpen(true)} />
        <ActionRow label={t('accountCard.changePassword')} onClick={() => setPasswordModalOpen(true)} />
        <ActionRow
          label={t('feedback.title')}
          onClick={() => setFeedbackModalOpen(true)}
          icon={<MessageSquare className="w-4 h-4 text-stone-400" strokeWidth={2.5} />}
        />
        <ActionRow
          label={signingOut ? t('accountCard.signingOut') : t('accountCard.signOut')}
          hideBorder
          onClick={handleSignOut}
          icon={signingOut ? <Loader2 className="w-4 h-4 text-stone-400 animate-spin" /> : undefined}
        />
      </div>
      <ChangeEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} />
      <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />
      <FeedbackModal open={feedbackModalOpen} onClose={() => setFeedbackModalOpen(false)} />
    </>
  );
}

function ActionRow({
  label,
  hideBorder,
  onClick,
  icon,
}: {
  label: string;
  hideBorder?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-between p-5 px-6 hover:bg-stone-50 transition-colors text-left ${
        !hideBorder ? 'border-b border-stone-50' : ''
      } rounded-3xl active:scale-[0.98]`}
    >
      <span className="text-[15px] font-bold text-stone-700 tracking-tight">{label}</span>
      {icon ?? <ChevronRight className="w-4 h-4 text-stone-400" strokeWidth={3} />}
    </button>
  );
}
